import { useEffect, useMemo, useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@project/ui";
import { Button } from "@project/ui";
import { Icon, formatShort } from "@project/ui";
import { DashboardBackLink } from "@apps/dashboard/components/layout/DashboardBackLink";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { useActiveHouse } from "@apps/dashboard/features/houses/active-house-context";
import { ErrorState } from "@apps/dashboard/components/states/ErrorState";
import { LoadingState } from "@apps/dashboard/components/states/LoadingState";
import { expenseEffectiveMinor } from "@apps/dashboard/lib/expense-effective-amount";
import {
  fetchExpenses,
  fetchVaultItems,
  homeExpenseQueryKeys,
} from "@apps/dashboard/lib/home-expense-api";
import { formatMinorAsCurrency } from "@apps/dashboard/lib/format-money";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import { trackDashboardEvent } from "@apps/dashboard/lib/analytics";

const DEBOUNCE_MS = 400;
const MIN_QUERY_LEN = 2;

export default function SearchPage(): JSX.Element {
  const prefersReducedMotion = useReducedMotion();
  const { t, locale } = useI18n();
  const { activeHouseId } = useActiveHouse();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQ = searchParams.get("q") ?? "";
  const [draft, setDraft] = useState(urlQ);

  useEffect(() => {
    setDraft(urlQ);
  }, [urlQ]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      const next = draft.trim();
      const cur = urlQ.trim();
      if (next === cur) {
        return;
      }
      if (next) {
        setSearchParams({ q: next }, { replace: true });
      } else {
        setSearchParams({}, { replace: true });
      }
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [draft, urlQ, setSearchParams]);

  const qApplied = urlQ.trim();
  const canSearch = qApplied.length >= MIN_QUERY_LEN;
  const showMinHint = draft.trim().length > 0 && draft.trim().length < MIN_QUERY_LEN;

  const expensesQuery = useQuery({
    queryKey:
      activeHouseId && canSearch
        ? homeExpenseQueryKeys.expenses(locale, activeHouseId, {
            sort: "date_desc",
            search: qApplied,
          })
        : ["search", "expenses", "off"],
    queryFn: () => {
      if (!activeHouseId) throw new Error("no house");
      return fetchExpenses(locale, activeHouseId, { sort: "date_desc", search: qApplied });
    },
    enabled: Boolean(activeHouseId && canSearch),
  });

  const vaultQuery = useQuery({
    queryKey: activeHouseId ? homeExpenseQueryKeys.vault(locale, activeHouseId) : ["search", "vault", "off"],
    queryFn: () => {
      if (!activeHouseId) throw new Error("no house");
      return fetchVaultItems(locale, activeHouseId);
    },
    enabled: Boolean(activeHouseId && canSearch),
  });

  const vaultMatches = useMemo(() => {
    const items = vaultQuery.data ?? [];
    if (!canSearch) {
      return [];
    }
    const needle = qApplied.toLowerCase();
    return items.filter((v) => {
      const name = v.name.toLowerCase();
      const notes = (v.notes ?? "").toLowerCase();
      const at = (v.assetType ?? "").toLowerCase();
      return name.includes(needle) || notes.includes(needle) || at.includes(needle);
    });
  }, [vaultQuery.data, qApplied, canSearch]);

  const expenses = expensesQuery.data ?? [];
  const loading = canSearch && (expensesQuery.isLoading || vaultQuery.isLoading);
  const error = expensesQuery.isError || vaultQuery.isError;

  useEffect(() => {
    if (!canSearch || loading) {
      return;
    }
    void trackDashboardEvent("search_executed", {
      platform: "web",
      query_length: qApplied.length,
      expense_results: expenses.length,
      vault_results: vaultMatches.length,
    });
    if (expenses.length === 0 && vaultMatches.length === 0) {
      void trackDashboardEvent("search_empty_results", {
        platform: "web",
        query_length: qApplied.length,
      });
    }
  }, [canSearch, loading, qApplied.length, expenses.length, vaultMatches.length]);

  return (
    <div className={DASHBOARD_PAGE_CONTAINER}>
      <DashboardBackLink to={dashboardPaths.home}>{t("dashboardUi.searchPage.backHome")}</DashboardBackLink>
      <h1 className="font-headline text-2xl font-bold tracking-tight text-primary">
        {t("dashboardUi.pages.search")}
      </h1>
      <p className="mt-2 font-body text-body-md text-on-surface-variant">{t("dashboardUi.searchPage.subtitle")}</p>

      <div className="mt-8">
        <label htmlFor="global-search-input" className="sr-only">
          {t("common.search")}
        </label>
        <div className="relative">
          <Icon
            name="Search"
            className="pointer-events-none absolute left-4 top-1/2 size-5 -translate-y-1/2 text-on-surface-variant"
            strokeWidth={1.5}
            aria-hidden
          />
          <input
            id="global-search-input"
            type="search"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t("dashboardUi.shell.searchPlaceholder")}
            autoComplete="off"
            className="font-body text-body-md w-full rounded-2xl border border-outline-variant/25 bg-surface-container-lowest py-3.5 pl-12 pr-4 text-on-surface shadow-sm placeholder:text-on-surface-variant focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
        {showMinHint ? (
          <p className="mt-2 text-label-md text-on-surface-variant">{t("dashboardUi.searchPage.minCharsHint")}</p>
        ) : null}
      </div>

      {!activeHouseId ? (
        <div className="mt-10 rounded-2xl border border-dashed border-outline-variant/30 bg-surface-container-lowest/50 px-6 py-10 text-center">
          <p className="font-body text-body-md text-on-surface-variant">{t("dashboardUi.searchPage.noActiveHouse")}</p>
          <Button className="mt-4" asChild>
            <Link to={dashboardPaths.houses}>{t("dashboardUi.nav.houses")}</Link>
          </Button>
        </div>
      ) : null}

      {activeHouseId && canSearch ? (
        <div className="mt-10">
          {loading ? (
            <LoadingState />
          ) : error ? (
            <ErrorState
              onRetry={() => {
                void expensesQuery.refetch();
                void vaultQuery.refetch();
              }}
            />
          ) : (
            <>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <p className="text-label-md text-on-surface-variant">
                  {t("dashboardUi.searchPage.resultsFor", { query: qApplied })}
                </p>
                <Button variant="outline" size="sm" className="font-headline text-xs" asChild>
                  <Link
                    to={{
                      pathname: dashboardPaths.houseExpenses(activeHouseId),
                      search: `?q=${encodeURIComponent(qApplied)}`,
                    }}
                  >
                    {t("dashboardUi.searchPage.openJournalWithQuery")}
                  </Link>
                </Button>
              </div>

              {expenses.length === 0 && vaultMatches.length === 0 ? (
                <div className="rounded-2xl bg-surface-container-low px-6 py-12 text-center outline outline-1 outline-outline-variant/15">
                  <p className="font-body text-body-md text-on-surface-variant">{t("dashboardUi.searchPage.noResults")}</p>
                </div>
              ) : (
                <div className="flex flex-col gap-10">
                  {expenses.length > 0 ? (
                    <section aria-labelledby="search-expenses-heading">
                      <h2
                        id="search-expenses-heading"
                        className="font-headline text-sm font-bold uppercase tracking-wide text-primary"
                      >
                        {t("dashboardUi.searchPage.sectionExpenses")}
                      </h2>
                      <ul className="mt-3 flex flex-col gap-2">
                        {expenses.map((e, index) => {
                          const eff = expenseEffectiveMinor(e);
                          const showForecastHint =
                            e.actualAmountMinor != null && e.actualAmountMinor !== e.amountMinor;
                          return (
                            <motion.li
                              key={e.id}
                              initial={prefersReducedMotion ? undefined : { opacity: 0, y: 8 }}
                              animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
                              transition={
                                prefersReducedMotion
                                  ? undefined
                                  : {
                                      duration: 0.2,
                                      delay: Math.min(index, 6) * 0.03,
                                      ease: [0.22, 1, 0.36, 1],
                                    }
                              }
                            >
                              <Link
                                to={dashboardPaths.houseExpenseDetail(activeHouseId, e.id)}
                                onClick={() =>
                                  void trackDashboardEvent("search_result_opened", {
                                    platform: "web",
                                    result_type: "expense",
                                  })
                                }
                                className="flex flex-col gap-1 rounded-2xl bg-surface-container-low px-4 py-3 outline outline-1 outline-outline-variant/15 transition-colors hover:bg-surface-container-lowest/90 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                              >
                                <div className="min-w-0">
                                  <span className="font-headline font-bold text-on-surface">{e.title}</span>
                                  {showForecastHint ? (
                                    <p className="text-label-md text-on-surface-variant">
                                      {t("dashboardUi.expenses.listForecastVsActual", {
                                        amount: formatMinorAsCurrency(e.amountMinor, e.currency, locale),
                                      })}
                                    </p>
                                  ) : null}
                                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-label-md text-on-surface-variant">
                                    <span>{formatShort(e.occurredOn)}</span>
                                    {e.category?.name ? <span>{e.category.name}</span> : null}
                                  </div>
                                </div>
                                <span className="shrink-0 font-headline text-sm font-bold text-primary">
                                  {formatMinorAsCurrency(eff, e.currency, locale)}
                                </span>
                              </Link>
                            </motion.li>
                          );
                        })}
                      </ul>
                    </section>
                  ) : null}

                  {vaultMatches.length > 0 ? (
                    <section aria-labelledby="search-vault-heading">
                      <h2
                        id="search-vault-heading"
                        className="font-headline text-sm font-bold uppercase tracking-wide text-primary"
                      >
                        {t("dashboardUi.searchPage.sectionVault")}
                      </h2>
                      <ul className="mt-3 flex flex-col gap-2">
                        {vaultMatches.map((v, index) => (
                          <motion.li
                            key={v.id}
                            initial={prefersReducedMotion ? undefined : { opacity: 0, y: 8 }}
                            animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
                            transition={
                              prefersReducedMotion
                                ? undefined
                                : {
                                    duration: 0.2,
                                    delay: Math.min(index, 6) * 0.03,
                                    ease: [0.22, 1, 0.36, 1],
                                  }
                            }
                          >
                            <Link
                              to={dashboardPaths.houseVaultDetail(activeHouseId, v.id)}
                              onClick={() =>
                                void trackDashboardEvent("search_result_opened", {
                                  platform: "web",
                                  result_type: "vault",
                                })
                              }
                              className="flex flex-col gap-0.5 rounded-2xl bg-surface-container-low px-4 py-3 outline outline-1 outline-outline-variant/15 transition-colors hover:bg-surface-container-lowest/90"
                            >
                              <span className="font-headline font-bold text-on-surface">{v.name}</span>
                              {v.warrantyEndsOn ? (
                                <span className="text-label-md text-on-surface-variant">
                                  {t("dashboardUi.vault.cardExpiryDate")}: {formatShort(v.warrantyEndsOn)}
                                </span>
                              ) : null}
                            </Link>
                          </motion.li>
                        ))}
                      </ul>
                    </section>
                  ) : null}
                </div>
              )}
            </>
          )}
        </div>
      ) : null}

      {activeHouseId && !canSearch && draft.trim().length === 0 ? (
        <p className="mt-10 text-center font-body text-body-md text-on-surface-variant">
          {t("dashboardUi.searchPage.emptyPrompt")}
        </p>
      ) : null}
    </div>
  );
}
